/**
 * Web Push subscription helpers (Phase 20).
 * Requires: HTTPS, browser support for Notification + PushManager.
 */
import { api } from "./api";

const SW_URL = "/sw.js";

export function isPushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export async function ensureServiceWorker() {
  if (!isPushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return await navigator.serviceWorker.register(SW_URL);
}

export async function getSubscriptionStatus() {
  if (!isPushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      subscribed = !!sub;
    }
  } catch (_) {}
  return { supported: true, permission, subscribed };
}

export async function subscribePush() {
  if (!isPushSupported()) throw new Error("Push non supporté sur ce navigateur");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Autorisation refusée");
  const reg = await ensureServiceWorker();
  if (!reg) throw new Error("Service worker indisponible");
  await navigator.serviceWorker.ready;

  const { data } = await api.get("/push/vapid-public-key");
  if (!data?.public_key) throw new Error("Clé VAPID non configurée");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  const raw = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
  });
  return raw;
}

export async function unsubscribePush() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try { await api.post("/push/unsubscribe", { endpoint }); } catch (_) {}
  return true;
}

export async function sendPushTest(payload = {}) {
  return (await api.post("/push/test", payload)).data;
}
